import { GoogleGenerativeAI } from '@google/generative-ai';

class AIAnalyzer {
  constructor() {
    const apiKey = 'AIzaSyBsL3wB1htuLFwRTAH-d8Qg4ISTmgdV4s4';
    this.genAI = new GoogleGenerativeAI(apiKey);
  }

  detectPlatform(url) {
    const patterns = {
      instagram: /(?:https?:\/\/)?(?:www\.)?instagram\.com\/([a-zA-Z0-9._]+)/,
      facebook: /(?:https?:\/\/)?(?:www\.)?facebook\.com\/([a-zA-Z0-9.]+)/,
      linkedin: /(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/([a-zA-Z0-9-]+)/,
      twitter: /(?:https?:\/\/)?(?:www\.)?(?:twitter|x)\.com\/([a-zA-Z0-9_]+)/,
      youtube: /(?:https?:\/\/)?(?:www\.)?youtube\.com\/@?([a-zA-Z0-9_-]+)/,
      tiktok: /(?:https?:\/\/)?(?:www\.)?tiktok\.com\/@([a-zA-Z0-9._]+)/
    };

    for (const [platform, pattern] of Object.entries(patterns)) {
      const match = url.match(pattern);
      if (match) {
        return { platform, username: match[1] };
      }
    }

    throw new Error('Unsupported platform. Please use Instagram, Facebook, LinkedIn, Twitter, YouTube, or TikTok URLs.');
  }

  async analyzeProfile(url, email) {
    try {
      const { platform, username } = this.detectPlatform(url);
      
      // Simulate processing time for meter animation (3-5 seconds)
      await new Promise(resolve => setTimeout(resolve, 3000 + Math.random() * 2000));
      
      const analysis = this.generateMockAnalysis(platform, username);
      await this.sendEmailReport(email, analysis, url);
      return this.formatAnalysis(analysis, platform, username);
    } catch (error) {
      console.error('Analysis error:', error);
      throw error;
    }
  }

  getPlatformSpecificMetrics(platform) {
    const metrics = {
      instagram: {
        credibility: [
          { key: 'profileAuthenticity', name: 'Profile Authenticity', weight: 1.2, focus: 'Visual consistency and bio quality' },
          { key: 'accountHistory', name: 'Account History', weight: 1.0, focus: 'Account age and posting history' },
          { key: 'contentQuality', name: 'Content Quality', weight: 1.5, focus: 'Photo/video quality and aesthetics' },
          { key: 'engagementAuthenticity', name: 'Engagement Authenticity', weight: 1.3, focus: 'Like/comment ratio analysis' },
          { key: 'followerQuality', name: 'Follower Quality', weight: 1.1, focus: 'Real vs bot follower detection' },
          { key: 'hashtagStrategy', name: 'Hashtag Strategy', weight: 0.9, focus: 'Hashtag relevance and reach' }
        ],
        reach: [
          { key: 'storyEngagement', name: 'Story Engagement', weight: 1.2, focus: 'Story views and interactions' },
          { key: 'reelsPerformance', name: 'Reels Performance', weight: 1.4, focus: 'Reels views and virality' },
          { key: 'postReach', name: 'Post Reach', weight: 1.1, focus: 'Average post impressions' },
          { key: 'followerGrowth', name: 'Follower Growth', weight: 1.0, focus: 'Growth rate and retention' }
        ]
      },
      facebook: {
        credibility: [
          { key: 'profileCompleteness', name: 'Profile Completeness', weight: 1.1, focus: 'Complete profile information' },
          { key: 'pageAuthenticity', name: 'Page Authenticity', weight: 1.3, focus: 'Page verification and reviews' },
          { key: 'contentDiversity', name: 'Content Diversity', weight: 1.0, focus: 'Mix of posts, photos, videos' },
          { key: 'communityEngagement', name: 'Community Engagement', weight: 1.2, focus: 'Comments and shares quality' },
          { key: 'responseRate', name: 'Response Rate', weight: 1.1, focus: 'Message and comment responses' },
          { key: 'pageTransparency', name: 'Page Transparency', weight: 0.9, focus: 'Page history and ownership' }
        ],
        reach: [
          { key: 'postReach', name: 'Post Reach', weight: 1.2, focus: 'Organic vs paid reach' },
          { key: 'videoViews', name: 'Video Views', weight: 1.3, focus: 'Video watch time and completion' },
          { key: 'shareability', name: 'Shareability', weight: 1.1, focus: 'Post shares and virality' },
          { key: 'groupActivity', name: 'Group Activity', weight: 0.9, focus: 'Group participation and admin' }
        ]
      },
      linkedin: {
        credibility: [
          { key: 'professionalProfile', name: 'Professional Profile', weight: 1.4, focus: 'Complete work history and skills' },
          { key: 'connectionQuality', name: 'Connection Quality', weight: 1.2, focus: 'Professional network relevance' },
          { key: 'contentExpertise', name: 'Content Expertise', weight: 1.5, focus: 'Industry knowledge demonstration' },
          { key: 'endorsements', name: 'Endorsements & Recommendations', weight: 1.1, focus: 'Skills endorsements quality' },
          { key: 'activityConsistency', name: 'Activity Consistency', weight: 1.0, focus: 'Regular professional posting' },
          { key: 'thoughtLeadership', name: 'Thought Leadership', weight: 1.3, focus: 'Original insights and articles' }
        ],
        reach: [
          { key: 'postEngagement', name: 'Post Engagement', weight: 1.3, focus: 'Likes, comments, reposts' },
          { key: 'articleViews', name: 'Article Views', weight: 1.2, focus: 'Long-form content reach' },
          { key: 'networkGrowth', name: 'Network Growth', weight: 1.0, focus: 'Connection growth rate' },
          { key: 'industryInfluence', name: 'Industry Influence', weight: 1.4, focus: 'Sector-specific impact' }
        ]
      },
      twitter: {
        credibility: [
          { key: 'accountAuthenticity', name: 'Account Authenticity', weight: 1.3, focus: 'Verification and account age' },
          { key: 'tweetQuality', name: 'Tweet Quality', weight: 1.2, focus: 'Original vs retweet ratio' },
          { key: 'conversationQuality', name: 'Conversation Quality', weight: 1.1, focus: 'Reply engagement quality' },
          { key: 'followerRatio', name: 'Follower/Following Ratio', weight: 1.0, focus: 'Balanced follower ratio' },
          { key: 'contentConsistency', name: 'Content Consistency', weight: 1.1, focus: 'Regular tweeting pattern' },
          { key: 'mediaMix', name: 'Media Mix', weight: 0.9, focus: 'Text, images, videos balance' }
        ],
        reach: [
          { key: 'retweetRate', name: 'Retweet Rate', weight: 1.3, focus: 'Content amplification' },
          { key: 'impressions', name: 'Impressions', weight: 1.2, focus: 'Tweet visibility' },
          { key: 'engagementRate', name: 'Engagement Rate', weight: 1.4, focus: 'Likes, replies, retweets' },
          { key: 'trendParticipation', name: 'Trend Participation', weight: 1.0, focus: 'Trending topic involvement' }
        ]
      },
      youtube: {
        credibility: [
          { key: 'channelAuthority', name: 'Channel Authority', weight: 1.4, focus: 'Subscriber count and verification' },
          { key: 'videoQuality', name: 'Video Quality', weight: 1.5, focus: 'Production value and editing' },
          { key: 'contentOriginality', name: 'Content Originality', weight: 1.3, focus: 'Unique vs copied content' },
          { key: 'uploadConsistency', name: 'Upload Consistency', weight: 1.1, focus: 'Regular upload schedule' },
          { key: 'communityInteraction', name: 'Community Interaction', weight: 1.0, focus: 'Creator replies and engagement' },
          { key: 'thumbnailOptimization', name: 'Thumbnail Optimization', weight: 0.9, focus: 'Click-worthy thumbnails' }
        ],
        reach: [
          { key: 'viewCount', name: 'View Count', weight: 1.3, focus: 'Average views per video' },
          { key: 'watchTime', name: 'Watch Time', weight: 1.4, focus: 'Audience retention rate' },
          { key: 'subscriberGrowth', name: 'Subscriber Growth', weight: 1.2, focus: 'Monthly subscriber increase' },
          { key: 'videoShares', name: 'Video Shares', weight: 1.1, focus: 'Share and embed rate' }
        ]
      },
      tiktok: {
        credibility: [
          { key: 'profileAuthenticity', name: 'Profile Authenticity', weight: 1.2, focus: 'Bio and profile setup' },
          { key: 'contentOriginality', name: 'Content Originality', weight: 1.4, focus: 'Original vs duet/stitch' },
          { key: 'trendParticipation', name: 'Trend Participation', weight: 1.3, focus: 'Trending sound usage' },
          { key: 'videoQuality', name: 'Video Quality', weight: 1.1, focus: 'Video editing and effects' },
          { key: 'consistency', name: 'Posting Consistency', weight: 1.0, focus: 'Regular posting frequency' },
          { key: 'communityGuidelines', name: 'Community Guidelines', weight: 1.2, focus: 'Adherence to platform rules' }
        ],
        reach: [
          { key: 'forYouPageRate', name: 'For You Page Rate', weight: 1.5, focus: 'FYP algorithm success' },
          { key: 'videoViews', name: 'Video Views', weight: 1.3, focus: 'Average views per video' },
          { key: 'engagementRate', name: 'Engagement Rate', weight: 1.4, focus: 'Likes, comments, shares' },
          { key: 'followerGrowth', name: 'Follower Growth', weight: 1.1, focus: 'Viral growth potential' }
        ]
      }
    };

    return metrics[platform] || metrics.instagram; // Default to Instagram metrics
  }

  generateMockAnalysis(platform, username) {
    // Generate base score between 40-65%
    const baseScore = 40 + Math.floor(Math.random() * 26); // Random between 40-65
    const followerCount = Math.floor(Math.random() * 100000) + 5000;
    const platformMetrics = this.getPlatformSpecificMetrics(platform);
    
    // Generate credibility metrics based on platform with scores between 40-65%
    const credibilityMetrics = {};
    platformMetrics.credibility.forEach(metric => {
      const randomVariation = Math.random() * 15 - 7.5; // ±7.5% variation
      const score = Math.max(40, Math.min(65, Math.floor(baseScore + randomVariation)));
      credibilityMetrics[metric.key] = {
        score: score,
        reasoning: `${metric.focus}. Score based on ${platform} best practices.`,
        status: this.getScoreStatus(score),
        weight: metric.weight
      };
    });

    // Generate reach metrics based on platform with scores between 40-65%
    const reachMetrics = {};
    platformMetrics.reach.forEach(metric => {
      const randomVariation = Math.random() * 12 - 6; // ±6% variation
      const score = Math.max(40, Math.min(65, Math.floor(baseScore + randomVariation)));
      reachMetrics[metric.key] = {
        score: score,
        reasoning: `${metric.focus}. Performance indicators for ${platform}.`,
        status: this.getScoreStatus(score),
        weight: metric.weight
      };
    });
    
    return {
      profileOverview: {
        platform,
        username,
        verified: Math.random() > 0.7,
        followers: followerCount,
        following: Math.floor(followerCount * 0.3),
        posts: Math.floor(Math.random() * 500) + 100,
        bio: `${platform.charAt(0).toUpperCase() + platform.slice(1)} content creator`,
        avgLikes: Math.floor(followerCount * 0.05),
        avgComments: Math.floor(followerCount * 0.01)
      },
      credibilityMetrics: credibilityMetrics,
      reachMetrics: reachMetrics,
      overallScores: {
        credibility: baseScore + 5,
        reach: baseScore - 3,
        overall: baseScore
      },
      redFlags: Math.random() > 0.8 ? [{
        severity: "low",
        type: "Engagement Pattern",
        message: "Slight variation detected"
      }] : [],
      aiInsights: {
        contentThemes: [`${platform} lifestyle`, "Personal branding", "Community"],
        targetAudience: `18-34 year olds interested in ${platform} content`,
        strengths: ["Authentic voice", "High quality", "Good engagement"],
        weaknesses: ["Could diversify formats", "Engagement optimization"],
        authenticity: baseScore + 8,
        contentQualityScore: baseScore + 12,
        summary: `This ${platform} account shows strong authentic growth and engagement with quality content.`
      },
      recommendations: [
        { type: "success", message: "Continue consistent posting schedule" },
        { type: "info", message: "Experiment with video content formats" },
        { type: "info", message: "Engage more in comment sections" }
      ]
    };
  }

  async sendEmailReport(email, analysis, url) {
    console.log(`📧 Report sent to: ${email}`);
    console.log(`Profile: ${url}`);
    console.log(`Score: ${analysis.overallScores.overall}/100`);
    return true;
  }

  formatAnalysis(data, platform, username) {
    // Get first metric from credibilityMetrics for fallback scores
    const firstCredMetric = Object.values(data.credibilityMetrics)[0] || { score: 50 };
    
    return {
      profileData: data.profileOverview,
      credibilityMetrics: this.formatMetrics(data.credibilityMetrics),
      reachMetrics: this.formatMetrics(data.reachMetrics),
      overallScores: data.overallScores,
      redFlags: data.redFlags,
      aiInsights: {
        ...data.aiInsights,
        scores: {
          originality: firstCredMetric.score,
          relevance: firstCredMetric.score,
          professionalism: firstCredMetric.score,
          value: data.aiInsights.contentQualityScore,
          authenticity: data.aiInsights.authenticity
        },
        topics: data.aiInsights.contentThemes,
        overallScore: data.aiInsights.contentQualityScore
      },
      recommendations: data.recommendations
    };
  }

  formatMetrics(metrics) {
    const formatted = {};
    for (const [key, value] of Object.entries(metrics)) {
      formatted[key] = {
        score: value.score,
        maxScore: 100,
        percentage: value.score,
        details: {
          score: value.score,
          reasoning: value.reasoning,
          status: this.getScoreStatus(value.score),
          ...value
        }
      };
    }
    return formatted;
  }

  getScoreStatus(score) {
    if (score >= 85) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 50) return 'Average';
    if (score >= 30) return 'Below Average';
    return 'Poor';
  }
}

export default AIAnalyzer;
